import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * The title of the page on screen, shown in the header tab next to the menu button, as the
 * reference does ("Digital Card", "Ticketing System"). A page sets it through `PageHeader`
 * or `usePageTitle`.
 */
interface PageTitle {
  title: string;
  setTitle: (title: string) => void;
}

const PageTitleContext = createContext<PageTitle | null>(null);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState('');
  const value = useMemo(() => ({ title, setTitle }), [title]);
  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

/** Title currently shown in the header tab. Empty outside the app shell. */
export const useCurrentPageTitle = (): string => useContext(PageTitleContext)?.title ?? '';

/** Sets the header tab title while the calling page is mounted. */
export function usePageTitle(title: string): void {
  const ctx = useContext(PageTitleContext);
  const setTitle = ctx?.setTitle;
  useEffect(() => {
    if (!setTitle) return undefined;
    setTitle(title);
    return () => setTitle('');
  }, [title, setTitle]);
}
